

(() => {
  const MQ_MOBILE = '(max-width: 1279px)';
  const initDesktop = () => {
    document.querySelectorAll('[data-mega-tab]').forEach((tab) => {
      const trigger = /** @type {HTMLElement | null} */ (tab.querySelector('[data-mega-tab-trigger]'));
      const panel = /** @type {HTMLElement | null} */ (tab.querySelector('[data-mega-tab-panel]'));
      if (!trigger || !panel) return;

      panel.removeAttribute('hidden');

      /** @param {boolean} val */
      const setExpanded = (val) => trigger.setAttribute('aria-expanded', String(val));

      tab.addEventListener('mouseenter', () => setExpanded(true));
      tab.addEventListener('mouseleave', () => {
        if (!tab.contains(document.activeElement)) setExpanded(false);
      });
      tab.addEventListener('focusin', () => setExpanded(true));
      tab.addEventListener('focusout', (e) => {
        const fe = /** @type {FocusEvent} */ (e);
        const next = /** @type {Node | null} */ (fe.relatedTarget);
        if (!tab.contains(next)) setExpanded(false);
      });

      trigger.addEventListener('keydown', (e) => {
        const ke = /** @type {KeyboardEvent} */ (e);
        if (ke.key === 'Escape') {
          setExpanded(false);
          trigger.blur();
        }
      });

      panel.addEventListener('keydown', (e) => {
        const ke = /** @type {KeyboardEvent} */ (e);
        if (ke.key === 'Escape') {
          setExpanded(false);
          trigger.focus();
        }
      });
    });
  };

  // Move mobile parts emitted by each mega-tab block into the mobile nav containers,
  // and clone the desktop panel HTML into each mobile panel shell.
  const hydrateMobileNav = () => {
    document.querySelectorAll('[data-mega-mobile]').forEach((root) => {
      const tabsContainer = root.querySelector('[data-mega-mobile-tabs]');
      const panelsContainer = root.querySelector('[data-mega-mobile-panels]');
      if (!tabsContainer || !panelsContainer) return;
      if (tabsContainer.childElementCount > 0) return;

      document.querySelectorAll('[data-mega-mobile-parts]').forEach((parts) => {
        const tab = parts.querySelector('[data-mega-mobile-tab]');
        const panel = /** @type {HTMLElement | null} */ (parts.querySelector('[data-mega-mobile-panel]'));
        if (panel) {
          const sourceId = panel.dataset.megaMobileCloneFrom;
          const source = sourceId ? document.getElementById(sourceId) : null;
          if (source) panel.innerHTML = source.innerHTML;
        }
        if (tab) tabsContainer.appendChild(tab);
        if (panel) panelsContainer.appendChild(panel);
        parts.remove();
      });

      if (tabsContainer.childElementCount > 0) tabsContainer.setAttribute('role', 'tablist');
    });
  };

  // Mobile/tablet: the tab strip overflows, so let a mouse drag pan it like touch does.
  /** @param {Element | null} strip */
  const initTabDragScroll = (strip) => {
    if (!(strip instanceof HTMLElement) || strip.dataset.dragScroll === 'true') return;
    strip.dataset.dragScroll = 'true';

    let pressed = false;
    let dragged = false;
    let startX = 0;
    let startScrollLeft = 0;

    strip.addEventListener('pointerdown', (e) => {
      const pe = /** @type {PointerEvent} */ (e);
      if (pe.pointerType !== 'mouse' || pe.button !== 0) return;
      pressed = true;
      dragged = false;
      startX = pe.clientX;
      startScrollLeft = strip.scrollLeft;
    });

    strip.addEventListener('pointermove', (e) => {
      if (!pressed) return;
      const pe = /** @type {PointerEvent} */ (e);
      const delta = pe.clientX - startX;
      if (!dragged) {
        if (Math.abs(delta) < 5) return;
        dragged = true;
        strip.classList.add('is-dragging');
        strip.setPointerCapture(pe.pointerId);
      }
      pe.preventDefault();
      strip.scrollLeft = startScrollLeft - delta;
    });

    const endDrag = (e) => {
      if (!pressed) return;
      pressed = false;
      strip.classList.remove('is-dragging');
      const pe = /** @type {PointerEvent} */ (e);
      if (pe.pointerId != null && strip.hasPointerCapture(pe.pointerId)) strip.releasePointerCapture(pe.pointerId);
    };
    ['pointerup', 'pointercancel'].forEach((type) => strip.addEventListener(type, endDrag));

    // A drag that ends on a tab must not also activate it.
    strip.addEventListener(
      'click',
      (e) => {
        if (!dragged) return;
        dragged = false;
        e.preventDefault();
        e.stopPropagation();
      },
      { capture: true }
    );

    strip.addEventListener('dragstart', (e) => e.preventDefault());
  };

  // Mobile: tab switcher. Each [data-mega-mobile] root has tabs that swap which panel is visible.
  const initMobile = () => {
    hydrateMobileNav();
    document.querySelectorAll('[data-mega-mobile]').forEach((root) => {
      initTabDragScroll(root.querySelector('[data-mega-mobile-tabs-scroller]'));
      const tabs = /** @type {HTMLElement[]} */ (Array.from(root.querySelectorAll('[data-mega-mobile-tab]')));
      const panels = /** @type {HTMLElement[]} */ (Array.from(root.querySelectorAll('[data-mega-mobile-panel]')));
      // Pick the first BUTTON tab (link tabs are <a> and have no panel to show).
      const firstButton = tabs.find((t) => t.tagName === 'BUTTON');
      if (!firstButton) return;

      const initialId = firstButton.dataset.megaMobileTarget;

      /**
       * @param {string | undefined} tabId
       * @param {boolean} [focusTab]
       */
      const select = (tabId, focusTab = false) => {
        if (!tabId) return;
        tabs.forEach((t) => {
          if (t.tagName !== 'BUTTON') return;
          const isActive = t.dataset.megaMobileTarget === tabId;
          t.setAttribute('aria-selected', String(isActive));
          t.setAttribute('tabindex', isActive ? '0' : '-1');
          if (focusTab && isActive) t.focus();
        });
        panels.forEach((p) => {
          p.hidden = p.dataset.megaMobileId !== tabId;
        });
        if (root instanceof HTMLElement) root.scrollTop = 0;
      };

      if (initialId) select(initialId);

      tabs.forEach((tab, i) => {
        if (tab.tagName !== 'BUTTON') return;
        tab.addEventListener('click', () => select(tab.dataset.megaMobileTarget));
        tab.addEventListener('keydown', (e) => {
          const ke = /** @type {KeyboardEvent} */ (e);
          if (ke.key !== 'ArrowRight' && ke.key !== 'ArrowLeft' && ke.key !== 'Home' && ke.key !== 'End') return;
          ke.preventDefault();
          let next = i;
          if (ke.key === 'ArrowRight') next = (i + 1) % tabs.length;
          else if (ke.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
          else if (ke.key === 'Home') next = 0;
          else if (ke.key === 'End') next = tabs.length - 1;
          const nextTab = tabs[next];
          if (nextTab) select(nextTab.dataset.megaMobileTarget, true);
        });
      });
    });
  };

  const mqMobile = window.matchMedia(MQ_MOBILE);
  let mobileHydrated = false;

  const hydrateMobileOnce = () => {
    if (mobileHydrated) return;
    mobileHydrated = true;
    initMobile();
  };

  /** @param {Event} e */
  const isBurgerEvent = (e) => e.target instanceof Element && !!e.target.closest('.burger__button');

  const run = () => {
    if (mqMobile.matches) {
      hydrateMobileOnce();
      return;
    }

    initDesktop();

    // The burger nav is display:none on desktop, so cloning every mega panel into it up front
    // is wasted DOM and main-thread time. Wait until it can actually be seen.
    mqMobile.addEventListener('change', (e) => {
      if (e.matches) hydrateMobileOnce();
    });
    ['pointerdown', 'focusin'].forEach((type) => {
      document.addEventListener(
        type,
        (e) => {
          if (isBurgerEvent(e)) hydrateMobileOnce();
        },
        { capture: true }
      );
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
